import type { Order } from "@/queries/order.ts";
import { DateTimeFormatter } from "@js-joda/core";
import {
    Link,
    ListItem,
    ListItemText,
    type TypographyProps,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

type Props = {
    order: Order;
};

const dateFormatter = DateTimeFormatter.ofPattern("MM/dd/yyyy");

const statusLabels: Record<Order["status"], string> = {
    fabric_not_received: "Fabric not received",
    on_hold: "On hold",
    in_production: "In production",
    completed: "Completed",
};

const overflowTypographyProps: TypographyProps<"span", { component?: "span" }> = {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
};

const MobileOrderListItem = ({ order }: Props): ReactNode => {
    return (
        <ListItem disablePadding>
            <ListItemText
                primary={order.projectName}
                secondary={
                    order.tracking ? (
                        <Link href={order.tracking.trackingUrl} target="_blank" rel="noreferrer">
                            {order.tracking.carrierName} #${order.tracking.trackingNumber}
                        </Link>
                    ) : (
                        // Needs to be a non-empty string, otherwise it causes a layout shift
                        " "
                    )
                }
                primaryTypographyProps={overflowTypographyProps}
                secondaryTypographyProps={overflowTypographyProps}
            />
            <ListItemText
                primary={order.dueOn?.format(dateFormatter) ?? "No due date"}
                primaryTypographyProps={{
                    variant: "body2",
                }}
                secondary={statusLabels[order.status]}
                sx={{
                    textAlign: "right",
                    width: 150,
                    minWidth: 150,
                    maxWidth: 150,
                }}
            />
        </ListItem>
    );
};

const DesktopOrderListItem = ({ order }: Props): ReactNode => {
    return (
        <ListItem disablePadding>
            <ListItemText
                primary={order.projectName}
                primaryTypographyProps={overflowTypographyProps}
                sx={{ mr: 2 }}
            />
            {order.tracking && (
                <ListItemText
                    primary={
                        <Link href={order.tracking.trackingUrl} target="_blank" rel="noreferrer">
                            {order.tracking.carrierName} #${order.tracking.trackingNumber}
                        </Link>
                    }
                    primaryTypographyProps={overflowTypographyProps}
                    sx={{ width: 300, minWidth: 300, maxWidth: 300, mr: 2 }}
                />
            )}
            <ListItemText
                primary={statusLabels[order.status]}
                sx={{ width: 150, minWidth: 150, maxWidth: 150 }}
            />
            <ListItemText
                primary={order.dueOn?.format(dateFormatter) ?? "No due date"}
                sx={{ width: 100, minWidth: 150, maxWidth: 150, textAlign: "right" }}
            />
        </ListItem>
    );
};

const OrderListItem = ({ order }: Props): ReactNode => {
    const theme = useTheme();
    const useDesktopListItem = useMediaQuery(theme.breakpoints.up("md"));

    return useDesktopListItem ? (
        <DesktopOrderListItem order={order} />
    ) : (
        <MobileOrderListItem order={order} />
    );
};

export default OrderListItem;
